import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, GeneratedPost, Usage, Profile } from '../types';
import { supabase } from '../lib/supabase';

interface AppContextType {
  user: User | null;
  login: (email: string, name?: string) => void;
  logout: () => void;
  posts: GeneratedPost[];
  notifications: any[];
  subscription: any | null;
  billingHistory: any[];
  profile: Profile | null;
  addPost: (post: GeneratedPost) => void;
  deletePost: (id: string) => Promise<void>;
  markNotificationAsRead: (id: string) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
  usage: Usage;
  isLoading: boolean;
  refreshData: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [posts, setPosts] = useState<GeneratedPost[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [subscription, setSubscription] = useState<any | null>(null);
  const [billingHistory, setBillingHistory] = useState<any[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [usage, setUsage] = useState<Usage>({ generationsUsed: 0, planLimit: 10 });
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async (userId: string) => {
    try {
      // Fetch posts
      const { data: postsData } = await supabase
        .from('posts').select('*').eq('user_id', userId).order('created_at', { ascending: false });
      if (postsData) {
        setPosts(postsData.map(p => ({
          id: p.id,
          topic: p.topic,
          postType: p.post_type,
          tone: p.tone,
          audience: p.audience,
          hook: p.hook,
          caption: p.caption,
          hashtags: p.hashtags,
          imagePrompt: p.image_prompt,
          carouselSlides: p.carousel_slides,
          createdAt: p.created_at,
        })));
      }
    } catch (e) { console.warn('posts fetch failed:', e); }

    try {
      // Fetch usage metrics
      const { data: usageData } = await supabase
        .from('usage_metrics').select('*').eq('user_id', userId).single();
      if (usageData) {
        // Check if monthly reset is due (based on last_reset date)
        const lastReset = new Date(usageData.last_reset);
        const now = new Date();
        const daysSinceReset = (now.getTime() - lastReset.getTime()) / (1000 * 60 * 60 * 24);

        if (daysSinceReset >= 30) {
          // Reset usage for this billing cycle
          await supabase
            .from('usage_metrics')
            .update({ generations_used: 0, last_reset: now.toISOString() })
            .eq('user_id', userId);
          setUsage({ generationsUsed: 0, planLimit: usageData.plan_limit });
          console.log('Monthly usage reset applied.');
        } else {
          setUsage({ generationsUsed: usageData.generations_used, planLimit: usageData.plan_limit });
        }
      }
    } catch (e) { console.warn('usage_metrics not available yet — using defaults.'); }

    try {
      // Fetch notifications
      const { data: notifsData } = await supabase
        .from('notifications').select('*').eq('user_id', userId).order('created_at', { ascending: false }).limit(20);
      if (notifsData) {
        setNotifications(notifsData.map(n => ({
          id: n.id, title: n.title, message: n.message,
          type: n.type, isRead: n.is_read, createdAt: n.created_at
        })));
      }
    } catch (e) { console.warn('notifications fetch failed:', e); }

    try {
      // Fetch profile
      const { data: profileData } = await supabase
        .from('profiles').select('*').eq('id', userId).single();
      if (profileData) {
        setProfile({
          id: profileData.id,
          full_name: profileData.full_name,
          bio: profileData.bio,
          location: profileData.location,
          avatarUrl: profileData.avatar_url,
          plan: profileData.plan,
          createdAt: profileData.created_at,
        });
        setUser(prev => prev ? { ...prev, name: profileData.full_name || prev.name, plan: profileData.plan || prev.plan } : null);
      }
    } catch (e) { console.warn('profiles fetch failed:', e); }

    try {
      // Fetch subscription
      const { data: subData } = await supabase
        .from('subscriptions').select('*').eq('user_id', userId).single();
      if (subData) setSubscription(subData);
    } catch (e) { console.warn('subscriptions not available yet.'); }

    try {
      // Fetch billing history
      const { data: billingData } = await supabase
        .from('billing_history').select('*').eq('user_id', userId).order('created_at', { ascending: false });
      if (billingData) setBillingHistory(billingData);
    } catch (e) { console.warn('billing_history not available yet.'); }
  };

  useEffect(() => {
    // On first load, check for an existing session from localStorage
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser({
          id: session.user.id,
          name: session.user.user_metadata?.full_name || 'User',
          email: session.user.email || '',
          plan: 'Free',
        });
        fetchData(session.user.id);
      } else {
        setIsLoading(false);
      }
    });

    // Listen for future auth events (login, logout, token refresh)
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        setUser({
          id: session.user.id,
          name: session.user.user_metadata?.full_name || 'User',
          email: session.user.email || '',
          plan: 'Free',
        });
        fetchData(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        // Only clear state on an explicit logout
        setUser(null);
        setPosts([]);
        setNotifications([]);
        setSubscription(null);
        setBillingHistory([]);
        setProfile(null);
        setUsage({ generationsUsed: 0, planLimit: 10 });
        setIsLoading(false);
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Session refreshed silently — no need to re-fetch data
        setUser(prev => prev ? { ...prev } : null);
      }
    });

    return () => {
      authSub.unsubscribe();
    };
  }, []);

  // Set loading to false once initial data fetch is attempted
  useEffect(() => {
    if (user && posts.length >= 0) {
      setIsLoading(false);
    }
  }, [user, posts]);

  const login = (email: string, name: string = 'Creator') => { };

  const logout = async () => {
    await supabase.auth.signOut();
  };

  const addPost = (post: GeneratedPost) => {
    setPosts([post, ...posts]);
    setUsage((prev) => ({ ...prev, generationsUsed: prev.generationsUsed + 1 }));
  };

  const deletePost = async (id: string) => {
    const { error } = await supabase.from('posts').delete().eq('id', id);
    if (error) {
      console.error('Error deleting post:', error);
      return;
    }
    setPosts(posts.filter((p) => p.id !== id));
  };

  const markNotificationAsRead = async (id: string) => {
    const { error } = await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    if (!error) {
      setNotifications(notifications.map(n => n.id === id ? { ...n, isRead: true } : n));
    }
  };

  const markAllNotificationsAsRead = async () => {
    if (!user) return;
    const { error } = await supabase.from('notifications').update({ is_read: true }).eq('user_id', user.id);
    if (!error) {
      setNotifications(notifications.map(n => ({ ...n, isRead: true })));
    }
  };

  return (
    <AppContext.Provider value={{
      user, login, logout, posts, notifications, subscription, billingHistory, profile,
      addPost, deletePost, markNotificationAsRead, markAllNotificationsAsRead,
      usage, isLoading,
      refreshData: () => user ? fetchData(user.id) : Promise.resolve()
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
